import {
  Button,
  Fade,
  Menu,
  MenuProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { Info, Logout, OverlaySpinner, Plus } from '@ankr.com/ui';
import { Link } from 'react-router-dom';
import { t } from '@ankr.com/common';

import { Placeholder } from 'modules/common/components/Placeholder';
import { ScrollableContainer } from 'modules/common/components/ScrollableContainer';
import { teamsRoutesConfig } from 'domains/teams/routes';

import { AccountItem } from '../AccountItem';
import { PersonalAccountInfo } from '../PersonalAccountInfo';
import { useAccountsMenu } from './hooks/useAccountsMenu';
import { useAccountsMenuStyles } from './useAccountsMenuStyles';

export type AccountsMenuProps = Pick<MenuProps, 'anchorEl' | 'open'> & {
  onClose: () => void;
};

export const AccountsMenu = (props: AccountsMenuProps) => {
  const { onClose, open } = props;

  const {
    accounts,
    accountsLoading,
    handleGoToSettings,
    isAccountCreationAllowed,
    signOut,
  } = useAccountsMenu({ isAccountsMenuOpened: open, onClose });

  const { classes, cx } = useAccountsMenuStyles();

  return (
    <Menu
      TransitionComponent={Fade}
      anchorOrigin={{
        vertical: 48,
        horizontal: 'right',
      }}
      classes={{
        list: classes.list,
      }}
      disableScrollLock
      slotProps={{ paper: { className: classes.paper } }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...props}
    >
      <PersonalAccountInfo onAccountButtonClick={handleGoToSettings} />
      <hr className={classes.divider} />
      <div className={classes.accountsList}>
        <Typography variant="subtitle3" pl={2}>
          {t('account-menu.accounts')}
        </Typography>
        <Placeholder
          hasPlaceholder={accountsLoading}
          placeholder={<OverlaySpinner size={30} />}
        >
          <ScrollableContainer autoHeight autoHide>
            {accounts.map(account => (
              <AccountItem
                account={account}
                key={account.address}
                onSelect={onClose}
              />
            ))}
          </ScrollableContainer>
        </Placeholder>
      </div>
      <div className={classes.accountActions}>
        <div className={classes.createTeamButtonWrapper}>
          <Button
            className={classes.actionButton}
            component={Link}
            disabled={!isAccountCreationAllowed}
            size="small"
            startIcon={<Plus />}
            onClick={onClose}
            to={teamsRoutesConfig.newTeam.generatePath()}
            variant="text"
          >
            {t('account-menu.create-team-button')}
          </Button>
          {!isAccountCreationAllowed && (
            <Tooltip title={t('account-menu.limit-tooltip')}>
              <Info className={classes.infoIcon} />
            </Tooltip>
          )}
        </div>
      </div>
      <div className={classes.accountMenuFooter}>
        <Button
          className={cx(classes.actionButton, classes.signOutButton)}
          startIcon={<Logout />}
          onClick={signOut}
          variant="text"
          size="small"
        >
          {t('account-menu.sign-out')}
        </Button>
      </div>
    </Menu>
  );
};
